import { Avatar, Box, Chip, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import {
  type TrackingChangeModel,
  type OrganizationTrackingChangeGroupModel
} from '../../../models/TrackingChange'
import { MoreHoriz } from '@mui/icons-material'
import { useState, type MouseEvent } from 'react'
import { ResolveTrackingChange } from '../../../services/TrackingChangeService'
import { toast } from 'react-toastify'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../Routes'
import { useNavigate, useParams } from 'react-router-dom'
import { ImplementationDetailsTabs } from './ImplementationDetails'
import { PlanOrganizationTaskName } from '../../../models/PlanOrganizationTask'

interface Props {
  organizationChangeModel: OrganizationTrackingChangeGroupModel
  onChangeUpdate: (updatedChange: TrackingChangeModel) => void
  changeTab: (tab: number) => void
  onResolved?: () => void
}

const capitalize = (value: string): string =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value

export const RecentChangesList = (props: Props): JSX.Element => {
  const { rcnId, planId } = useParams()
  const nav = useNavigate()

  const [selectedChange, setSelectedChange] = useState<TrackingChangeModel | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const actionsMenuOpen = Boolean(anchorEl)

  const navigateToTask = (taskName: string, planOrganizationId: number, planOrgTaskId: number): void => {
    if (rcnId == null || planId == null) return

    if (taskName === PlanOrganizationTaskName.RcnOperations) {
      props.changeTab(Number(ImplementationDetailsTabs.RcnOperations))
      return
    }

    const route = generatePath(
      AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY,
      {
        rcnId,
        planId,
        tab: String(ImplementationDetailsTabs.PriorityActivities),
        planOrgId: String(planOrganizationId),
        planOrgTaskId: String(planOrgTaskId)
      }
    )
    nav(route)
  }

  const handleMenuClick = (e: MouseEvent<HTMLButtonElement>, change: TrackingChangeModel): void => {
    setAnchorEl(e.currentTarget)
    setSelectedChange(change)
  }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
    setSelectedChange(null)
  }

  const handleResolve = async (change: TrackingChangeModel): Promise<void> => {
    const { success, errors } = await ResolveTrackingChange({ id: change.id, isResolved: !change.isResolved })

    if (!success) {
      errors.forEach((e) => toast.error(e))
      return
    }

    props.onChangeUpdate({ ...change, isResolved: !change.isResolved })
    props.onResolved?.()
    handleMenuClose()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {props.organizationChangeModel.taskGroups.map(taskGroup => {
        return <Box key={taskGroup.taskId}>
          <Box sx={{ backgroundColor: 'var(--blue-50)', borderWidth: '1px 0 1px 0', borderColor: 'var(--blue-300)', borderStyle: 'solid', color: 'var(--grey-900)', textAlign: 'center' }}>
            <Typography variant='h5' padding='10px'>
              {props.organizationChangeModel.organizationName} - {taskGroup.taskName}
            </Typography>
          </Box>

          {taskGroup.changes.map(change => {
            return <Box data-testid='orgTaskTrackingChange' key={change.id} sx={{ padding: '12px', display: 'flex', gap: '12px' }}>
              <Avatar src={change.actingUser?.imageKey} sx={{ width: '32px', height: '32px', marginTop: '10px' }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography variant='h5'>
                      {change.itemLabel.length > 0 ? change.itemLabel : change.entityType}
                    </Typography>
                    <Chip size='small' label={capitalize(change.changeType)} />
                  </Box>

                  <IconButton
                    aria-controls={actionsMenuOpen ? 'change-actions-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={actionsMenuOpen ? 'true' : undefined}
                    aria-label='actions menu'
                    data-testid='changeActionButton'
                    onClick={(e) => { handleMenuClick(e, change) }}
                  >
                    <MoreHoriz />
                  </IconButton>
                </Box>

                <Typography variant='body2' sx={{ color: 'var(--grey-700)' }}>
                  {change.entityType}
                  {change.actingUser != null ? ` - ${change.actingUser.firstName} ${change.actingUser.lastName}` : ''}
                </Typography>

                <Typography variant='body1' sx={{ color: 'var(--grey-500)' }}>
                  {new Date(change.dateStamp).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>
          })}

        </Box>
      })}

      {selectedChange != null &&
        <Menu
          id='change-actions-menu'
          anchorEl={anchorEl}
          open={actionsMenuOpen}
          onClose={handleMenuClose}
          MenuListProps={{ 'aria-labelledby': 'change-actions-button' }}
        >
          <MenuItem
            data-testid='viewChangeActivity'
            onClick={() => {
              const group = props.organizationChangeModel.taskGroups.find(tg => tg.changes.some(c => c.id === selectedChange.id))
              if (group != null) navigateToTask(group.taskName, group.planOrganizationId, group.taskId)
              handleMenuClose()
            }}
          >
            View Activity
          </MenuItem>
          <MenuItem data-testid='toggleChangeResolve' onClick={() => { void handleResolve(selectedChange) }}>
            Mark {selectedChange.isResolved ? 'Unresolved' : 'Resolved'}
          </MenuItem>
        </Menu>
      }
    </Box>
  )
}
